import React, { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "../button";

interface ISearchBarProps {
  form: UseFormReturn<any, any>;
  onSubmit: () => void;
  placeholder?: string;
  hasAdvancedFilters?: boolean;
  advancedFilters?: React.ReactNode;
  minSearchLength?: number;
}

export const SearchBar: React.FC<ISearchBarProps> = ({
  form,
  onSubmit,
  placeholder = "Rechercher...",
  advancedFilters,
  minSearchLength = 3,
}) => {
  const values = form.getValues();
  const hasActiveAdvancedFilter = Object.keys(values).some(
    (key) => key !== "search" && !!values[key]
  );

  const [isFormOpened, setIsFormOpened] = useState(hasActiveAdvancedFilter);

  useEffect(() => {
    if (hasActiveAdvancedFilter) {
      setIsFormOpened(true);
    }
  }, [hasActiveAdvancedFilter]);

  const search = form.watch("search");

  const handleSubmit = () => {
    if (search && search.length > 0 && search.length < minSearchLength) {
      toast.error(`La recherche doit contenir au minimum ${minSearchLength} lettres`);
      return;
    }
    onSubmit();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-1 items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition-all">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
              placeholder={placeholder}
              autoComplete="off"
              {...form.register("search")}
              className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 text-sm placeholder-gray-400"
            />
          </div>
          {advancedFilters && (
            <button
              onClick={() => setIsFormOpened((prev) => !prev)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isFormOpened
                  ? "bg-primary-50 text-primary-600 hover:bg-primary-100"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
              title="Filtres avancés"
              type="button"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </button>
          )}
          <Button onClick={handleSubmit} size="sm">
            Rechercher
          </Button>
        </div>

        {advancedFilters && isFormOpened && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {advancedFilters}
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={handleSubmit} variant="primary" size="sm">
                Appliquer les filtres
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
